import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, ShieldAlert, Activity, Brain, HeartPulse, MoonStar, Dna, Flame } from "lucide-react";
import { api } from "../lib/api";
import { Marquee } from "../components/Marquee";

const CHAPTERS = [
  { n: "01", title: "Cellular Signaling & Rejuvenation", icon: Dna, text: "Peptides act as precise biological messengers, docking into cell receptors to trigger tissue repair, mitochondrial biogenesis, and protein synthesis without systemic disturbance." },
  { n: "02", title: "Targeted Molecular Efficacy", icon: Activity, text: "Unlike traditional broad-spectrum supplements, bio-engineered peptide sequences hit specific metabolic targets with micro-molar precision." },
  { n: "03", title: "Sustained Neuro & Physical Longevity", icon: Brain, text: "By stimulating endogenous hormones, growth factors, and antioxidants, Alpha Peps protocols support vital organs, joints, brain health, and metabolic elasticity." },
];

const PILLARS = [
  { icon: HeartPulse, title: "Recovery & Repair", text: "Accelerated connective tissue healing and reduced systemic inflammation after exertion." },
  { icon: Flame, title: "Metabolic Mastery", text: "Support for glycemic balance, lipid metabolism, and healthy body composition protocols." },
  { icon: Brain, title: "Cognitive Clarity", text: "Neuro-cognitive support targeting mental fog, focus, and long-term brain health." },
  { icon: MoonStar, title: "Deep Restorative Sleep", text: "Enhanced slow-wave and REM sleep quality through endogenous hormone optimization." },
];

export default function Benefits() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-16" data-testid="benefits-page">
      <section className="spotlight mx-auto max-w-7xl px-5 pb-16 pt-20 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">THE SCIENCE</div>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="benefits-heading">
            Health Benefits Manifesto
          </h1>
          <p className="mt-4 max-w-xl text-base text-slate-400">
            Three chapters on how precision peptide protocols support recovery, metabolism, cognition, and longevity.
          </p>
        </motion.div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28" data-testid="manifesto-chapters">
        {CHAPTERS.map((c, i) => (
          <motion.div
            key={c.n}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={`grid items-center gap-10 border-t border-white/8 py-16 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
            data-testid={`benefits-chapter-${c.n}`}
          >
            <div>
              <span className="text-glow font-display text-7xl font-extrabold text-[#6E7683]/40 sm:text-8xl">{c.n}</span>
              <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">{c.title}</h2>
            </div>
            <div className="glass rounded-2xl p-8">
              <c.icon className="h-7 w-7 text-[#E8E9EB]" />
              <p className="mt-5 text-base leading-relaxed text-slate-300">{c.text}</p>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="border-t border-white/5 bg-[#0A0A0C] px-5 py-20 sm:px-8 lg:py-28" data-testid="wellness-pillars">
        <div className="mx-auto max-w-7xl">
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">GENERAL WELLNESS</div>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Four pillars of optimization
          </h2>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="glass rounded-2xl p-6 transition-colors duration-300 hover:border-[#E8E9EB]/30"
                data-testid={`pillar-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p.icon className="h-6 w-6 text-[#E8E9EB]" />
                <h3 className="mt-4 font-display text-lg font-semibold text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28" data-testid="per-product-benefits">
        <div className="font-mono text-[11px] tracking-[0.3em] text-[#E8E9EB]">PER-FORMULATION</div>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
          Benefits by protocol
        </h2>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {products.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
              className="glass rounded-2xl p-7"
              data-testid={`benefit-card-${p.id}`}
            >
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-display text-xl font-semibold text-white">{p.name}</h3>
                <span className="shrink-0 font-mono text-[10px] tracking-[0.2em] text-[#E8E9EB]">{p.code}</span>
              </div>
              <p className="mt-1 text-sm text-slate-500">{p.subtitle}</p>
              <ul className="mt-5 flex flex-col gap-2.5">
                {p.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#6E7683]" /> {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="tracing glass rounded-2xl p-8 sm:p-10"
          data-testid="medical-disclaimer"
        >
          <div className="flex items-start gap-4">
            <ShieldAlert className="mt-1 h-6 w-6 shrink-0 text-amber-400" />
            <div>
              <h3 className="font-display text-lg font-semibold text-white">Critical Medical Disclaimer</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">
                Products sold by Alpha Peps are intended solely for laboratory research, educational, or therapeutic
                evaluation under professional supervision. Statements on this site have not been evaluated by the FDA.
                Products are not intended to diagnose, treat, cure, or prevent any medical condition. Always consult a
                qualified healthcare professional before beginning any supplementation protocol.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
